<template>
  <div class="in-app-purchase-product-modal row">
    <ElForm
      ref="productForm"
      :model="product"
      :rules="productRules"
      label-position="top"
      class="in-app-purchase-product-modal__form"
      @submit.native.prevent="submitProduct"
    >
      <ElFormItem :label="$t('APP.IAB.PRODUCT_TYPE')" prop="type">
        <ElRadioGroup
          v-model="type"
          class="in-app-purchase-product-modal__type"
          :disabled="isUpdateMode"
        >
          <div class="row">
            <div class="columns">
              <ElRadio
                label="subscription"
                border
                class="in-app-purchase-product-modal__type-radio"
              >
                {{ $t('APP.IAB.SUBSCRIPTION_PROMOTE.TITLE') }}
              </ElRadio>
            </div>

            <div class="columns">
              <ElRadio
                label="one_time"
                border
                class="in-app-purchase-product-modal__type-radio"
              >
                {{ $t('APP.IAB.PURCHASABLE') }}
              </ElRadio>
            </div>
          </div>
        </ElRadioGroup>
      </ElFormItem>

      <ElFormItem
        v-if="type === 'subscription'"
        prop="kind"
        :label="$t('APP.IAB.SUBSCRIPTION_TYPE')"
      >
        <AlternativeSelect
          v-model="product.kind"
          :items="kindLabels"
          :key-prop="item => item[0]"
          :label-prop="item => $t(item[1])"
          :value-prop="item => item[0]"
        />
      </ElFormItem>

      <ElFormItem :label="$t('APP.IAB.PRODUCT_SKU')" prop="sku">
        <ElInput
          v-model="product.sku"
          type="text"
          name="sku"
          required
          class="text-latin"
          dir="ltr"
          :disabled="processing || isUpdateMode"
        />
      </ElFormItem>

      <div class="row">
        <div class="columns">
          <ElFormItem :label="$t('APP.IAB.PRODUCT_TITLE_FA')" prop="title_fa">
            <ElInput
              v-model="product.title_fa"
              type="text"
              name="title_fa"
              required
              :disabled="processing"
            />
          </ElFormItem>
        </div>

        <div class="columns">
          <ElFormItem :label="$t('APP.IAB.PRODUCT_TITLE_EN')" prop="title_en">
            <ElInput
              v-model="product.title_en"
              type="text"
              name="title_en"
              required
              class="text-latin"
              dir="ltr"
              :disabled="processing"
            />
          </ElFormItem>
        </div>
      </div>

      <ElFormItem :label="$t('APP.IAB.PRODUCT_DESC_FA')" prop="description_fa">
        <ElInput
          v-model="product.description_fa"
          type="text"
          name="description_fa"
          :disabled="processing"
        />
      </ElFormItem>

      <ElFormItem :label="$t('APP.IAB.PRODUCT_DESC_EN')" prop="description_en">
        <ElInput
          v-model="product.description_en"
          type="text"
          name="description_en"
          class="text-latin"
          dir="ltr"
          :disabled="processing"
        />
      </ElFormItem>

      <ElFormItem
        :label="`${$t('APP.IAB.PRODUCT_PRICE')} ${$t('TAX_INCLUSIVE')}`"
        prop="price"
      >
        <NumericInput
          v-model="product.price"
          name="price"
          :append="$t('RIALS')"
          :disabled="processing"
          required
          dir="rtl"
          with-separator
        />
      </ElFormItem>

      <div class="flex-container align-justify">
        <div
          v-if="isProductsDigital"
          v-show="product.price > 0"
          class="flex-child-grow"
        >
          <p class="text-secondary">
            {{ $t('APPS.IN_APP_PRODUCT.LOW_SALES_TITLE') }}
          </p>
          <div>
            <span class="text-secondary">
              {{ $t('APPS.INFO.DEVS_SHARE') }}:
            </span>
            <span class="nowrap">
              {{ priceFormat(devShare) }} {{ $t('RIALS') }}
            </span>
          </div>
          <div>
            <span class="text-secondary">
              {{ $t('APPS.INFO.BAZAAR_SHARE') }}:
            </span>
            <span class="nowrap">
              {{ priceFormat(bazaarShare) }} {{ $t('RIALS') }}
            </span>
          </div>
          <div>
            <span class="text-secondary">{{ $t('APPS.INFO.VAT_VALUE') }}:</span>
            <span class="nowrap">
              {{ priceFormat(vatValue) }} {{ $t('RIALS') }}
            </span>
          </div>
          <div>
            <span class="text-secondary">{{ $t('کارمزد درگاه') }}:</span>
            <span class="nowrap">
              {{ priceFormat(shaparakFee) }} {{ $t('RIALS') }}
            </span>
          </div>
        </div>

        <div
          v-show="product.price > 0"
          v-if="isProductsDigital"
          class="flex-child-grow"
        >
          <p class="text-secondary">
            {{ $t('APPS.IN_APP_PRODUCT.HIGH_SALES_TITLE') }}
          </p>
          <div>
            <span class="text-secondary">
              {{ $t('APPS.INFO.DEVS_SHARE') }}:
            </span>
            <span class="nowrap">
              {{ priceFormat(devShareHighSales) }} {{ $t('RIALS') }}
            </span>
          </div>
          <div>
            <span class="text-secondary">
              {{ $t('APPS.INFO.BAZAAR_SHARE') }}:
            </span>
            <span class="nowrap">
              {{ priceFormat(bazaarShareHighSales) }} {{ $t('RIALS') }}
            </span>
          </div>
          <div>
            <span class="text-secondary">{{ $t('APPS.INFO.VAT_VALUE') }}:</span>
            <span class="nowrap">
              {{ priceFormat(vatValue) }} {{ $t('RIALS') }}
            </span>
          </div>
          <div>
            <span class="text-secondary">{{ $t('کارمزد درگاه') }}:</span>
            <span class="nowrap">
              {{ priceFormat(shaparakFee) }} {{ $t('RIALS') }}
            </span>
          </div>
        </div>
      </div>

      <ElFormItem :label="$t('APP.IAB.PRODUCT_STATE')">
        <ElRadioGroup v-model="product.state">
          <ElRadio label="A" :disabled="processing">
            {{ $t('ACTIVE') }}
          </ElRadio>
          <ElRadio label="D" :disabled="processing">
            {{ $t('DISABLED') }}
          </ElRadio>
        </ElRadioGroup>
      </ElFormItem>

      <!-- dirty hack until ElementUI ends up with a solution -->
      <input v-show="false" type="submit" />
    </ElForm>

    <ErrorsList v-if="errors" :errors="errors" />

    <div class="in-app-purchase-product-modal__actions text-left">
      <ElButton
        type="text"
        :plain="true"
        @click.native.prevent="resetFormAndClose"
      >
        {{ $t('CANCEL') }}
      </ElButton>

      <ElButton
        type="primary"
        :loading="processing"
        @click.native.prevent="submitProduct"
      >
        {{ confirmButtonText }}
      </ElButton>
    </div>
  </div>
</template>

<script>
import form from '@/mixins/form';
import { format as priceFormat } from '@/utils/number';
import modal from '@/mixins/modal';
import priceShare from '@/mixins/price-share';
import packageName from '@/mixins/packageName';
import ErrorsList from '@@/common/ErrorsList';
import { kindLabels } from '@/utils/apps/kindLabel';
import AlternativeSelect from '@@/common/AlternativeSelect';
import NumericInput from '@/components/common/NumericInput.vue';

const DEFAULT_MODEL = {
  sku: '',
  title_fa: '',
  title_en: '',
  description_fa: '',
  description_en: '',
  price: '',
  state: 'A',
  kind: '',
};

export default {
  name: 'InAppPurchaseProductModal',

  components: {
    ErrorsList,
    AlternativeSelect,
    NumericInput,
  },

  mixins: [form, modal, priceShare, packageName],

  props: {
    confirmButtonText: {
      type: String,
      required: true,
    },
    originalProduct: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      type: 'subscription',
      product: { ...DEFAULT_MODEL },
      processing: false,
      errors: null,
    };
  },

  computed: {
    isUpdateMode() {
      return Boolean(this.originalProduct);
    },
    kindLabels() {
      return Object.entries(kindLabels).filter(([key]) => key != 'P');
    },
    productRules() {
      const rules = {
        required: true,
        message: this.$t('VALIDATION_REQUIRED_ERR'),
        trigger: 'blur',
      };

      return {
        kind: {
          ...rules,
          trigger: 'change',
        },
        sku: rules,
        title_fa: rules,
        title_en: rules,
        price: {
          ...rules,
        },
      };
    },
    isProductsDigital() {
      const { productsType } = this.$auth.user?.publisher || {};
      return productsType === 'digital';
    },
  },

  watch: {
    'originalProduct': {
      deep: true,
      immediate: true,
      handler() {
        this.resetData();
      },
    },
    'product.price': function watchPrice(newVal, oldVal) {
      if (newVal === oldVal) {
        return false;
      }

      // the "priceModel" is defined in the priceShare mixin
      this.priceModel = Number(newVal);
    },
    'type'() {
      if (this.type === 'one_time') {
        this.product.kind = 'P';
      } else {
        this.product.kind = this.originalProduct?.kind || '';
      }
    },
  },
  methods: {
    priceFormat,

    submitProduct() {
      if (!this.isValid('productForm')) {
        return false;
      }

      this.$emit('close', { ...this.product });
    },

    resetFormAndClose() {
      this.resetData();
      this.$emit('close');
    },

    resetData() {
      if (this.originalProduct) {
        this.product = { ...this.originalProduct };
        this.priceModel = this.originalProduct.price;
        this.type =
          this.originalProduct?.kind === 'P' ? 'one_time' : 'subscription';
      } else {
        this.product = { ...DEFAULT_MODEL };
        this.type = 'subscription';
        this.priceModel = 0;
        this.resetForm('productForm');
      }
    },
  },
};
</script>

<style lang="scss">
.in-app-purchase-product-modal {
  &__form,
  &__type,
  &__type-radio,
  &__actions {
    width: 100%;
  }

  .flex-child-grow {
    flex: 1 1 0;
  }
}
</style>
